import * as React from 'react';
import { useDispatch } from 'react-redux';
import { Button, RoundButton } from '@cian/ui-kit';
import { IconToggleHeartOn16, IconToggleHeartOff16 } from '@cian/ui-kit-design-tokens/icons';
import { debounce } from 'throttle-debounce';

import { TThunkDispatch } from '../../types/redux/thunk';
import { addNewbuildingFavorite, deleteNewbuildingFavorite } from '../../actions/newbuildingFavorite';
import { FAVORITE_DEBOUNCE_DELAY } from '../../constants/delays';
import { useAnalyticsContext } from '../../utils/analyticsContext';
import { TSellerType } from '../NewbuildingCallButton/tracking';

import * as style from './NewbuildingFavoriteButton.css';
import { trackAddFavoriteGallery } from './tracking';

export interface INewbuildingFavoriteButtonProps {
  newbuildingId: number;
  isFavorite: boolean;
  gaLabel?: string;
  url?: string;
  variant?: 'button' | 'round';
  sellerType?: TSellerType;
}

export const NewbuildingFavoriteButton: React.FC<INewbuildingFavoriteButtonProps> = ({
  newbuildingId,
  isFavorite,
  gaLabel = '',
  variant = 'round',
  url,
  sellerType,
}) => {
  const dispatch = useDispatch<TThunkDispatch>();
  const { placement } = useAnalyticsContext();

  const handleClick = React.useCallback(
    debounce(FAVORITE_DEBOUNCE_DELAY, () => {
      if (isFavorite) {
        dispatch(deleteNewbuildingFavorite({ newbuildingId, label: gaLabel }));
      } else {
        dispatch(addNewbuildingFavorite({ newbuildingId, label: gaLabel }));

        if (placement === 'gallery' && sellerType) {
          trackAddFavoriteGallery({ newbuildingId, url, sellerType });
        }
      }
    }),
    [dispatch, newbuildingId, isFavorite, gaLabel, url, sellerType, placement],
  );

  if (variant === 'button') {
    return (
      <Button
        beforeIcon={
          isFavorite ? (
            <IconToggleHeartOn16 color="icon-negative-default" />
          ) : (
            <IconToggleHeartOff16 color="current_color" />
          )
        }
        onClick={handleClick}
        size="M"
        theme="fill_secondary"
      >
        {isFavorite ? 'В избранном' : 'В избранное'}
      </Button>
    );
  }

  return (
    <RoundButton
      data-testid="newbuilding-favorite-button"
      icon={
        isFavorite ? (
          // RoundButton перебивает цвет иконки своим темовым цветом; обёртка в div не даёт ему достучаться до svg (хак).
          <div className={style['icon']}>
            <IconToggleHeartOn16 color="control-negative-primary-default" />
          </div>
        ) : (
          <IconToggleHeartOff16 />
        )
      }
      onClick={handleClick}
      size="M"
      theme="light"
      title="В избранное"
    />
  );
};
